import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import {
  requirementsService,
  type RequirementCategory,
  type RequirementListItem,
  type RequirementStatus,
  type RequirementDetail,
} from '@/services/requirements.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  title: string;
  description: string;
  category_id: string;
  status_id: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  quantity: number;
  unit: string;
  estimated_cost: string;
  due_date: string;
  fulfilled_date: string;
  rejection_reason: string;
  notes: string;
}

interface Props {
  open: boolean;
  requirement: RequirementListItem | null;
  onClose: () => void;
}

export function EditRequirementModal({ open, requirement, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery<RequirementCategory[]>({
    queryKey: ['requirements', 'categories'],
    queryFn: () => requirementsService.listCategories(),
  });

  const { data: statuses = [] } = useQuery<RequirementStatus[]>({
    queryKey: ['requirements', 'statuses'],
    queryFn: () => requirementsService.listStatuses(),
  });

  const { data: detail } = useQuery<RequirementDetail>({
    queryKey: ['requirements', 'detail', requirement?.id],
    queryFn: () => requirementsService.getRequirement(requirement!.id),
    enabled: open && !!requirement,
  });

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      category_id: '',
      status_id: '',
      priority: 'medium',
      quantity: 1,
      unit: 'pieces',
      estimated_cost: '',
      due_date: '',
      fulfilled_date: '',
      rejection_reason: '',
      notes: '',
    },
  });

  const priorityValue = watch('priority');
  const categoryValue = watch('category_id');
  const statusValue = watch('status_id');

  const currentStatus = statuses.find((s) => s.id === statusValue);
  const isRejected = currentStatus?.code === 'REJECTED';

  useEffect(() => {
    if (open && requirement) {
      reset({
        title: requirement.title,
        description: detail?.description ?? '',
        category_id: requirement.category?.id ?? '',
        status_id: requirement.status?.id ?? '',
        priority: requirement.priority,
        quantity: requirement.quantity,
        unit: requirement.unit,
        estimated_cost: requirement.estimated_cost?.toString() ?? '',
        due_date: requirement.due_date ?? '',
        fulfilled_date: requirement.fulfilled_date ?? '',
        rejection_reason: detail?.rejection_reason ?? '',
        notes: detail?.notes ?? '',
      });
    }
  }, [open, requirement, detail, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!requirement) return;
      await requirementsService.updateRequirement(requirement.id, {
        title: values.title.trim(),
        description: values.description.trim() || undefined,
        category_id: values.category_id || undefined,
        status_id: values.status_id || undefined,
        priority: values.priority,
        quantity: Number(values.quantity),
        unit: values.unit.trim(),
        estimated_cost: values.estimated_cost ? Number(values.estimated_cost) : undefined,
        due_date: values.due_date || undefined,
        fulfilled_date: values.fulfilled_date || undefined,
        rejection_reason: values.rejection_reason.trim() || undefined,
        notes: values.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Requirement updated');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'list'] });
      if (requirement) {
        void queryClient.invalidateQueries({ queryKey: ['requirements', 'detail', requirement.id] });
      }
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit Requirement</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-req-title">Title *</Label>
            <Input
              id="edit-req-title"
              {...register('title', { required: 'Title is required' })}
            />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-req-description">Description</Label>
            <Textarea id="edit-req-description" {...register('description')} rows={3} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={categoryValue} onValueChange={(v) => setValue('category_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" {...register('category_id')} />
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={statusValue} onValueChange={(v) => setValue('status_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input type="hidden" {...register('status_id')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select
              value={priorityValue}
              onValueChange={(v) => setValue('priority', v as FormValues['priority'])}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isRejected && (
            <div className="space-y-1.5">
              <Label htmlFor="edit-req-reject">Rejection Reason</Label>
              <Textarea
                id="edit-req-reject"
                {...register('rejection_reason')}
                placeholder="Reason for rejection"
                rows={2}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-req-qty">Quantity</Label>
              <Input id="edit-req-qty" type="number" min={1} step="any" {...register('quantity', { valueAsNumber: true })} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-req-unit">Unit</Label>
              <Input id="edit-req-unit" {...register('unit')} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-req-cost">Estimated Cost (₹)</Label>
              <Input id="edit-req-cost" type="number" min={0} step="any" {...register('estimated_cost')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-req-due">Due Date</Label>
            <Input id="edit-req-due" type="date" {...register('due_date')} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-req-fulfilled">Fulfilled Date</Label>
            <Input id="edit-req-fulfilled" type="date" {...register('fulfilled_date')} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-req-notes">Notes</Label>
            <Textarea id="edit-req-notes" {...register('notes')} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
